import axios from "axios";

import type {DefaultResponse} from "@/core/type/request.type";
import apiClient from "@/core/http/apiClient";
import eventBus from "@/app/events/eventBus";

/**
 * Fachada HTTP do sistema. Nenhum componente ou service fala com axios direto.
 *
 * Política de notificação do Prisma (diferente do New Prisma, por decisão explícita):
 * **o helper notifica**. Toda falha de requisição vira um toast disparado daqui via
 * `system:requestError`, a menos que o chamador passe `showToastError = false`.
 * O 401 é a exceção: quem notifica é o interceptor do `apiClient`, que também derruba
 * a sessão — notificar de novo aqui geraria toast duplicado.
 */
export const useRequestHelper = () => {

    const API_URL = import.meta.env.VITE_API_URL;
    const API_IMAGE_SERVICE_URL = import.meta.env.VITE_API_IMAGESERVICE_URL;

    function getErrorResponse(error: any): DefaultResponse<string> {

        let message = "";
        let errorMsg = "ERRO DESCONHECIDO";
        let auxData;

        //Verifica se a API retornou erro
        if (error.response) {

            //Verifica se é erro de Autenticacao
            if (error.response.status == 401) {
                errorMsg = "NÃO AUTENTICADO"

                //Verifica se é erro de Permissão
            } else if (error.response.status == 403) {
                errorMsg = "NÃO AUTORIZADO"
            } else if (error.response.status == 400) {

                if (error.response.data.data) {
                    errorMsg = verificaArrayMessage(error.response.data.data);
                }

                if (error.response.data.message) {
                    message = error.response.data.message.toString().toUpperCase();
                }

            } else if (error.response.data == "") {
                errorMsg = error.response.status + " - " + error.response.statusText.toString().toUpperCase();
            } else if (error.response.status == 500) {
                errorMsg = "SERVER ERROR \n" + error.response.data;
            } else {
                //Verifica se a API retornou erros
                if (error.response.data.data) {
                    errorMsg = error.response.data.data;
                }
            }

            if (error.response.data && error.response.data.auxData) {
                auxData = error.response.data.auxData;
            }
        }

        return {
            success: false,
            data: errorMsg.toString(),
            message: message,
            auxData: auxData
        };
    }

    function verificaArrayMessage(data: any): string {

        let mensagemRetorno = "";

        if (Array.isArray(data)) {

            //Itera o array
            data.forEach(e => {
                if (e.key && e.message) {
                    mensagemRetorno += (mensagemRetorno !== "" ? "\n" : "") +
                        e.key.toString().toUpperCase() + ' - ' + e.message.toString().toUpperCase();
                } else {
                    mensagemRetorno += (mensagemRetorno !== "" ? "\n" : "") +
                        e.toString();
                }
            })

        } else {
            //é um objeto
            //Procura pela chave KEY e MESSAGE e monta uma string de retorno
            if (data.key && data.message) {
                mensagemRetorno = data.key.toString().toUpperCase() + ' - ' + data.message.toString().toUpperCase();
            } else {
                mensagemRetorno = data.toString();
            }
        }

        return mensagemRetorno;
    }

    /** Normaliza o corpo de sucesso da API no `DefaultResponse<T>` que todo chamador espera. */
    function normalizarSucesso<Type>(response: any): DefaultResponse<Type> {
        return {
            data: response.data.data,
            success: response.data.success,
            message: response.data.message,
            auxData: response.data.auxData
        };
    }

    /**
     * Notifica a falha, salvo quando o interceptor já notificou (401) ou quando o
     * chamador pediu para tratar por conta própria.
     */
    function notificarFalha(error: any, showToastError: boolean) {
        if (!showToastError) return;
        if (error.response?.status === 401) return;

        eventBus.emit("system:requestError", getErrorResponse(error));
    }

    function sendRequestGet<Type>(url: string, withToken: boolean = true, showToastError = true): Promise<DefaultResponse<Type>> {
        return apiClient
            .get(url, {semToken: !withToken})
            .then(response => Promise.resolve(normalizarSucesso<Type>(response)))
            .catch(error => {
                notificarFalha(error, showToastError);
                return Promise.reject(getErrorResponse(error));
            });
    }

    function sendRequestGetBlob(url: string, withToken: boolean = true) {
        return apiClient.get(url, {
            semToken: !withToken,
            responseType: "blob"
        });
    }

    /**
     * POST que devolve arquivo (ex.: zip). Em sucesso resolve a resposta crua do axios (`data` é o Blob,
     * `headers` traz o Content-Disposition). Em falha o corpo também chega como Blob: ele é lido de volta
     * para o envelope JSON antes de notificar, para o toast mostrar a `message` do backend.
     */
    function sendRequestPostBlob(url: string, data: any, withToken: boolean = true, showToastError = true) {
        return apiClient
            .post(url, data, {semToken: !withToken, responseType: "blob"})
            .catch(async error => {
                if (error.response?.data instanceof Blob) {
                    try {
                        error.response.data = JSON.parse(await error.response.data.text());
                    } catch {
                        error.response.data = "";
                    }
                }
                notificarFalha(error, showToastError);
                return Promise.reject(getErrorResponse(error));
            });
    }

    /** Nome do arquivo vindo do Content-Disposition (`filename*=UTF-8''...` ou `filename=...`). */
    function getNomeArquivoResposta(headers: any, nomePadrao: string): string {
        const disposition: string | undefined = headers?.["content-disposition"];
        if (!disposition) return nomePadrao;

        const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
        if (utf8) return decodeURIComponent(utf8[1].trim());

        const simples = /filename="?([^";]+)"?/i.exec(disposition);
        return simples ? simples[1].trim() : nomePadrao;
    }

    function sendRequestPost<Type>(url: string, data: any, withToken: boolean = true, showToastError = true): Promise<DefaultResponse<Type>> {
        return apiClient
            .post(url, data, {semToken: !withToken})
            .then(response => Promise.resolve(normalizarSucesso<Type>(response)))
            .catch(error => {
                notificarFalha(error, showToastError);
                return Promise.reject(getErrorResponse(error));
            });
    }

    // Utilizado para a Solicitacao Geral devido aos arquivos
    function sendRequestPostForm<Type>(url: string, data: any, withToken: boolean = true, showToastError = true): Promise<DefaultResponse<Type>> {
        return apiClient
            // o interceptor do apiClient remove o Content-Type quando o corpo é FormData,
            // deixando o axios montar o boundary do multipart sozinho
            .post(url, data, {semToken: !withToken})
            .then(response => Promise.resolve(normalizarSucesso<Type>(response)))
            .catch(error => {
                notificarFalha(error, showToastError);
                return Promise.reject(getErrorResponse(error));
            });
    }

    // Chamadas a serviços externos (ex.: image service) — sem baseURL/token/interceptors do apiClient.
    function sendRequestPostGeneric(url: string, data: any) {
        return axios.post(
            url,
            data,
            {
                headers: {'Content-Type': 'application/json'}
            })
            .then(response => Promise.resolve(response.data))
            .catch(error => Promise.reject(error))
    }

    function sendRequestGetGeneric(url: string) {
        return axios.get(
            url,
            {
                headers: {'Content-Type': 'application/json'}
            })
            .then(response => Promise.resolve(response.data))
            .catch(error => Promise.reject(error))
    }

    return {
        API_URL,
        API_IMAGE_SERVICE_URL,
        sendRequestGet,
        sendRequestPost,
        sendRequestPostGeneric,
        sendRequestGetGeneric,
        sendRequestPostForm,
        sendRequestGetBlob,
        sendRequestPostBlob,
        getNomeArquivoResposta
    };
}
